import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Theme } from "@emotion/react";
import { Caption, Header3 } from "@iventis/styles/src/components/texts";
import { Node } from "@iventis/domain-model/model/node";
import { media, sidebarHeaderMixin, skeletonShimmer, styled } from "@iventis/styles";
import { CursorTooltip } from "@iventis/components";
import React, { BaseSyntheticEvent, FunctionComponent, useMemo } from "react";
import { UserScope } from "@iventis/domain-model/model/userScope";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "@iventis/utilities";
import { TreeBrowserQueryString } from "@iventis/tree-browser";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { RootDispatch, RootState } from "./state/root.store";
import { sidebar } from "./state/sidebar.slice";
import { PopoverComponents } from "./state/sidebar.slice.types";
import { getStaticLibraries } from "./plan-library/mapping-library.helpers";

export const SideBarHeaderComponent: FunctionComponent<{
    mapName: string;
    parentFolder: Node;
    treeId: string;
    setSidebarPopoverAnchor: (anchor: Element) => void;
    sidebarHidden: boolean;
    setSidebarHidden: (hidden: boolean) => void;
}> = ({ mapName, parentFolder, treeId, setSidebarPopoverAnchor, setSidebarHidden, sidebarHidden }) => {
    const dispatch: RootDispatch = useDispatch();
    const translate = useIventisTranslate();

    const userScope = useSelector((state: RootState) => state.auth.user.userScope);

    const openPopover = (e: BaseSyntheticEvent) => {
        setSidebarPopoverAnchor(e.currentTarget);
        dispatch(
            sidebar.actions.setPopover({
                component: PopoverComponents.MAP_SETTINGS,
                nodeId: null,
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "top",
                    horizontal: "center",
                },
            })
        );
    };

    const returnsToStaticLibrary = useMemo(() => getStaticLibraries().some((lib) => lib.id === parentFolder?.id), [parentFolder]);

    const returnLibraryId = returnsToStaticLibrary ? parentFolder?.id : treeId;

    return (
        <>
            <StyledHeader>
                <div className="text">
                    {mapName && parentFolder ? (
                        <>
                            {userScope !== UserScope.External && (
                                <StyledLink
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ opacity: "0.8" }}
                                    to={`/spatial-planner/${returnLibraryId}/${parentFolder.id === treeId ? "" : `?${TreeBrowserQueryString.MAIN_NODE_ID}=${parentFolder.id}`}`}
                                    data-cy="back-to-map-browser"
                                >
                                    <FontAwesomeIcon size="xs" icon="chevron-left" />
                                    <Caption style={{ paddingLeft: "0.5rem" }}>
                                        {Content.library2.mappingLibraries[parentFolder.name] ? (
                                            <span>{translate(Content.library2.mappingLibraries[parentFolder.name])}</span>
                                        ) : (
                                            <span>{parentFolder.name}</span>
                                        )}
                                    </Caption>
                                </StyledLink>
                            )}
                            <CursorTooltip text={mapName} enterDelay={800}>
                                <StyledMapName>{mapName}</StyledMapName>
                            </CursorTooltip>
                        </>
                    ) : (
                        <>
                            <div className="skeleton-back">Placeholder workspace</div>
                            <StyledMapName className="skeleton-title">Placeholder map</StyledMapName>
                        </>
                    )}
                </div>

                <div className="icons">
                    <InteractiveElement className="icon" onClick={openPopover}>
                        <span className="hidden">Settings</span>
                        <FontAwesomeIcon size="lg" icon="gear" />
                    </InteractiveElement>
                    <InteractiveElement onClick={() => setSidebarHidden(!sidebarHidden)} className="icon" data-testid={`${sidebarHidden ? "show" : "hide"}-sidebar-button`}>
                        <span className="hidden">Hide sidebar</span>
                        <StyledFontAwesomeIcon icon={sidebarHidden ? "chevrons-down" : "chevrons-up"} />
                    </InteractiveElement>
                </div>
            </StyledHeader>
        </>
    );
};

const StyledMapName = styled(Header3)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

const StyledHeader = styled.header`
    background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued70};
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 50;

    .text {
        padding-left: 1rem;
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .skeleton-back {
        ${({ theme }: { theme: Theme }) => skeletonShimmer({ sequence: 0, backgroundColour: theme.primaryColors.subdued70 })};
        opacity: 0.7;
        transform: scale(0.9);
    }

    .skeleton-title {
        ${({ theme }: { theme: Theme }) => skeletonShimmer({ sequence: 2, backgroundColour: theme.primaryColors.subdued70 })};
        opacity: 0.7;
        transform: scale(0.9);
    }

    .icons {
        display: flex;
        height: 100%;
        .icon {
            height: 100%;
            padding: 0.75rem;
            &:hover {
                color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focusAccent};
            }
        }
    }

    ${sidebarHeaderMixin};
`;

const StyledLink = styled(Link)`
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    display: flex;
    align-items: center;
    &:hover {
        text-decoration: underline;
    }
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
    ${media.medium} {
        transform: rotate(-90deg);
    }
`;
